import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { format } from 'date-fns';
import { useGatesNow } from '../GatesNowDataContext';
import {
    SEVERITY,
    STATUS,
    STATUS_CLOSED_REGULAR,
    STATUS_OPEN_OUTSIDE,
    classifyUnitDiscrepancy,
    formatRelativeSensorLabel,
    getSensorAgeMinutes,
    getTrustStatus,
    groupRowsByUnit,
    hasLiveDiscrepancy,
} from '../utils/gateStatus';

const TRUST_META = {
    [STATUS.DISCREPANCY]: { label: 'Partner variance', icon: 'fa-circle-xmark', kind: 'discrepancy' },
    [STATUS.UNKNOWN]: { label: 'Outdated', icon: 'fa-circle-question', kind: 'unknown' },
    [STATUS.OK]: { label: 'OK', icon: 'fa-circle-check', kind: 'ok' },
};

const TRUST_FILTER_OPTIONS = [STATUS.DISCREPANCY, STATUS.UNKNOWN, STATUS.OK].map((value) => ({
    label: TRUST_META[value].label,
    value,
}));

const STALE_LABEL = 'No recent sensor update';

/** Long option lists get a search box in the dropdown panel. */
const SEARCHABLE_MIN_OPTIONS = 8;

const EMPTY_FILTERS = {
    TrustStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    StatusLabel: { value: null, matchMode: FilterMatchMode.EQUALS },
    SQLTenantName: { value: null, matchMode: FilterMatchMode.EQUALS },
    SQLUnitName: { value: null, matchMode: FilterMatchMode.EQUALS },
    SQLLocationName: { value: null, matchMode: FilterMatchMode.EQUALS },
    GateNames: { value: null, matchMode: FilterMatchMode.CUSTOM },
};

/** Default view: status (worst first), then tenant A to Z. */
const DEFAULT_SORT = [
    { field: 'SortRank', order: 1 },
    { field: 'SQLTenantName', order: 1 },
];

/** Gate filter keeps any unit that contains the selected gate. */
FilterService.register('custom_GateNames', (gateNames, selected) => (
    !selected || (gateNames || []).includes(selected)
));

/** Ranks after the discrepancy severities (0 to 2): stale, then OK. */
const RANK_UNKNOWN = 3;
const RANK_OK = 4;

const TrustPill = ({ trust }) => {
    const meta = TRUST_META[trust] || TRUST_META[STATUS.OK];
    return (
        <span className={`trust-pill is-${meta.kind}`} title={meta.label}>
            <i className={`fa-solid ${meta.icon}`} aria-hidden="true" />
            <span className="trust-pill-label">{meta.label}</span>
        </span>
    );
};

/** Row cells: icon only; the description column already says what is wrong. */
const TrustIcon = ({ trust }) => {
    const meta = TRUST_META[trust] || TRUST_META[STATUS.OK];
    return (
        <span className={`trust-icon is-${meta.kind}`} title={meta.label} role="img" aria-label={meta.label}>
            <i className={`fa-solid ${meta.icon}`} aria-hidden="true" />
        </span>
    );
};

const SensorChip = ({ timestamp }) => {
    const { label, stale } = formatRelativeSensorLabel(timestamp);
    return (
        <span className={`sensor-chip${stale ? ' is-stale' : ''}`}>
            <i className="fa-regular fa-clock" aria-hidden="true" />
            {label}
        </span>
    );
};

const formatScheduleTime = (timeString) => {
    if (!timeString) return '';
    return format(new Date(`2000-01-01T${timeString}`), 'hh:mm a');
};

const plural = (count, one, many) => (count === 1 ? one : many);

const uniqueSorted = (items, key) =>
    Array.from(new Set(items.map((item) => item[key]).filter(Boolean))).sort();

const summarizeHealthyStates = (gates) => {
    const states = Array.from(new Set(gates.map((g) => g.StateDescription).filter(Boolean)));
    return states.length ? states.join(' / ') : 'Unknown';
};

const compareRankThenTenant = (a, b) => (
    a.SortRank - b.SortRank || (a.SQLTenantName || '').localeCompare(b.SQLTenantName || '')
);

const buildUnitRow = (unit) => {
    const { gates } = unit;
    const total = gates.length;
    const staleGates = gates.filter((g) => g.IsStale);
    const classification = hasLiveDiscrepancy(gates) ? classifyUnitDiscrepancy(gates) : null;
    const newest = gates.reduce(
        (best, g) => (getSensorAgeMinutes(g.Timestamp) < getSensorAgeMinutes(best.Timestamp) ? g : best),
        gates[0]
    );

    let TrustStatus = STATUS.OK;
    let SortRank = RANK_OK;
    let StatusLabel = summarizeHealthyStates(gates);
    let GateSummary = `${total} ${total === 1 ? 'gate' : 'gates'}`;

    if (classification) {
        TrustStatus = STATUS.DISCREPANCY;
        SortRank = classification.severity;
        StatusLabel = classification.statusLabel;
        GateSummary = classification.gateSummary;
    } else if (staleGates.length > 0) {
        TrustStatus = STATUS.UNKNOWN;
        SortRank = RANK_UNKNOWN;
        StatusLabel = STALE_LABEL;
        GateSummary = `${staleGates.length} of ${total} readings outdated`;
    }

    return {
        ...unit,
        TrustStatus,
        SortRank,
        Severity: classification?.severity ?? null,
        StatusLabel,
        GateSummary,
        GateCount: total,
        GateNames: gates.map((g) => g.PlaceName).filter(Boolean),
        LastTimestamp: newest?.Timestamp,
        LastAgeMinutes: getSensorAgeMinutes(newest?.Timestamp),
    };
};

/** Same color rules as the map: red full close, orange partial close, cyan open outside hours. */
const unitRowClassName = (unit) => {
    if (unit.Severity === SEVERITY.CLOSED_REGULAR) return 'row-closed';
    if (unit.Severity === SEVERITY.PARTIAL_CLOSED_REGULAR) return 'row-partial-closed';
    if (unit.Severity === SEVERITY.PARTIAL_OPEN_CLOSED_HOURS) return 'row-open-outside';
    return undefined;
};

const gateSortRank = (gate) => {
    if (gate.TrustStatus === STATUS.UNKNOWN) return RANK_UNKNOWN;
    if (gate.StateDescription === STATUS_CLOSED_REGULAR) return SEVERITY.CLOSED_REGULAR;
    if (gate.StateDescription === STATUS_OPEN_OUTSIDE) return SEVERITY.PARTIAL_OPEN_CLOSED_HOURS;
    return RANK_OK;
};

const gateRowsFor = (unit) => unit.gates
    .map((g) => ({ ...g, TrustStatus: getTrustStatus(g) }))
    .map((g) => ({ ...g, SortRank: gateSortRank(g) }))
    .sort((a, b) => a.SortRank - b.SortRank || (a.PlaceName || '').localeCompare(b.PlaceName || ''));

const statusTextBody = (unit) => (
    <span className={`unit-status-text${unit.Severity === SEVERITY.CLOSED_REGULAR ? ' is-closed' : ''}`}>
        {unit.StatusLabel}
    </span>
);

const gatesBody = (unit) => (
    <span className={`gate-count-chip${unit.Severity !== null ? ' is-alert' : ''}`}>
        <i className="fa-solid fa-door-open" aria-hidden="true" />
        {unit.GateSummary}
    </span>
);

const unitCodeBody = (unit) => <span className="unit-code">{unit.SQLUnitName}</span>;
const locationBody = (unit) => <span className="unit-location">{unit.SQLLocationName}</span>;
const timeBody = (value) => <span className="unit-time">{formatScheduleTime(value)}</span>;

const openStateBody = (value) => {
    if (!value) return null;
    const isOpen = /^open$/i.test(value);
    return <span className={`state-chip ${isOpen ? 'is-open' : 'is-closed'}`}>{value}</span>;
};

export const ConcessionsGateNow = () => {
    const { rows } = useGatesNow();
    const [isCompact, setIsCompact] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= 900
    );
    const [expandedRows, setExpandedRows] = useState({});
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [multiSortMeta, setMultiSortMeta] = useState(DEFAULT_SORT);

    const unitRows = useMemo(
        () => groupRowsByUnit(rows).map(buildUnitRow).sort(compareRankThenTenant),
        [rows]
    );

    useEffect(() => {
        const onResize = () => setIsCompact(window.innerWidth <= 900);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const filteredUnits = useMemo(() => unitRows.filter((item) => (
        (!filters.TrustStatus.value || item.TrustStatus === filters.TrustStatus.value) &&
        (!filters.StatusLabel.value || item.StatusLabel === filters.StatusLabel.value) &&
        (!filters.SQLTenantName.value || item.SQLTenantName === filters.SQLTenantName.value) &&
        (!filters.SQLUnitName.value || item.SQLUnitName === filters.SQLUnitName.value) &&
        (!filters.SQLLocationName.value || item.SQLLocationName === filters.SQLLocationName.value) &&
        (!filters.GateNames.value || item.GateNames.includes(filters.GateNames.value))
    )), [unitRows, filters]);

    const availableOptions = useMemo(() => ({
        TrustStatus: TRUST_FILTER_OPTIONS.filter((opt) =>
            filteredUnits.some((item) => item.TrustStatus === opt.value)
        ),
        StatusLabel: uniqueSorted(filteredUnits, 'StatusLabel'),
        SQLTenantName: uniqueSorted(filteredUnits, 'SQLTenantName'),
        SQLUnitName: uniqueSorted(filteredUnits, 'SQLUnitName'),
        SQLLocationName: uniqueSorted(filteredUnits, 'SQLLocationName'),
        GateNames: Array.from(new Set(filteredUnits.flatMap((item) => item.GateNames))).sort(),
    }), [filteredUnits]);

    const counts = useMemo(() => ({
        units: filteredUnits.length,
        gates: filteredUnits.reduce((sum, u) => sum + u.GateCount, 0),
        discrepancy: filteredUnits.filter((u) => u.TrustStatus === STATUS.DISCREPANCY).length,
        outdated: filteredUnits.filter((u) => u.TrustStatus === STATUS.UNKNOWN).length,
    }), [filteredUnits]);

    const hasActiveFilters = Object.values(filters).some((f) => f.value);
    const allExpanded = filteredUnits.length > 0 && filteredUnits.every((u) => expandedRows[u.id]);
    const isDefaultSort = JSON.stringify(multiSortMeta) === JSON.stringify(DEFAULT_SORT);

    const toggleUnit = useCallback((unit) => {
        setExpandedRows((prev) => {
            const next = { ...prev };
            if (next[unit.id]) delete next[unit.id];
            else next[unit.id] = true;
            return next;
        });
    }, []);

    const toggleAll = () => {
        if (allExpanded) {
            setExpandedRows({});
            return;
        }
        setExpandedRows(Object.fromEntries(filteredUnits.map((u) => [u.id, true])));
    };

    const resetView = () => {
        setFilters(EMPTY_FILTERS);
        setMultiSortMeta(DEFAULT_SORT);
    };

    const trustOptionTemplate = (option) => (option ? <TrustPill trust={option.value} /> : null);
    const trustValueTemplate = (option, props) => (option ? <TrustPill trust={option.value} /> : props.placeholder);

    const DropdownFilterTemplate = useCallback((filter) => {
        const opts = availableOptions[filter.field] || [];
        const isTrust = filter.field === 'TrustStatus';
        const value = filters[filter.field]?.value;
        return (
            <Dropdown
                value={value}
                options={opts}
                optionLabel={isTrust ? 'label' : undefined}
                optionValue={isTrust ? 'value' : undefined}
                itemTemplate={isTrust ? trustOptionTemplate : undefined}
                valueTemplate={isTrust ? trustValueTemplate : undefined}
                placeholder="All"
                filter={opts.length >= SEARCHABLE_MIN_OPTIONS}
                filterPlaceholder="Search"
                resetFilterOnHide
                emptyMessage="No options"
                emptyFilterMessage="No matches"
                onChange={(e) => {
                    setFilters((prev) => ({
                        ...prev,
                        [filter.field]: { ...prev[filter.field], value: e.value },
                    }));
                    filter.filterApplyCallback(e.value);
                }}
                showClear
                className={`gate-filter-dropdown${value ? ' is-active' : ''}`}
                panelClassName="gate-filter-panel"
            />
        );
    }, [availableOptions, filters]);

    const rowExpansionTemplate = useCallback((unit) => (
        <div className="unit-gates">
            <DataTable
                value={gateRowsFor(unit)}
                dataKey="PlaceName"
                size="small"
                className="unit-gates-table"
            >
                <Column header="Status" body={(g) => <TrustIcon trust={g.TrustStatus} />} style={{ width: '5rem' }} />
                <Column header="Gate" field="PlaceName" body={(g) => <span className="gate-name">{g.PlaceName}</span>} style={{ minWidth: '10rem' }} />
                <Column header="Status Description" field="StateDescription" style={{ minWidth: '12rem' }} />
                <Column
                    header="Scheduled"
                    body={(g) => openStateBody(g.ScheduledOpenState)}
                    alignHeader="center"
                    align="center"
                    style={{ width: '8rem' }}
                    headerClassName="col-state"
                    bodyClassName="col-state"
                />
                <Column
                    header="Actual"
                    body={(g) => openStateBody(g.ActualOpenState)}
                    alignHeader="center"
                    align="center"
                    style={{ width: '8rem' }}
                    headerClassName="col-state"
                    bodyClassName="col-state"
                />
                <Column header="Last Sensor Reading" body={(g) => <SensorChip timestamp={g.Timestamp} />} style={{ minWidth: '10rem' }} />
            </DataTable>
        </div>
    ), []);

    return (
        <div id="ConcessionsGateNow">
            <div className="gatesnow-table-card">
                <div className="table-toolbar">
                    <div className="table-toolbar-stats">
                        <span className="toolbar-stat">
                            <strong>{counts.units}</strong> {plural(counts.units, 'unit', 'units')}
                        </span>
                        <span className="toolbar-stat">
                            <strong>{counts.gates}</strong> {plural(counts.gates, 'gate', 'gates')}
                        </span>
                        <span className={`toolbar-stat is-discrepancy${counts.discrepancy ? '' : ' is-zero'}`}>
                            <span className="toolbar-dot" />
                            <strong>{counts.discrepancy}</strong> partner variance{counts.discrepancy === 1 ? '' : 's'}
                        </span>
                        <span className={`toolbar-stat is-unknown${counts.outdated ? '' : ' is-zero'}`}>
                            <span className="toolbar-dot" />
                            <strong>{counts.outdated}</strong> outdated
                        </span>
                    </div>
                    <div className="table-toolbar-actions">
                        {(hasActiveFilters || !isDefaultSort) && (
                            <button type="button" className="toolbar-btn is-ghost" onClick={resetView}>
                                <i className="fa-solid fa-rotate-left" aria-hidden="true" />
                                Reset view
                            </button>
                        )}
                        <button type="button" className="toolbar-btn" onClick={toggleAll} disabled={!filteredUnits.length}>
                            <i className={`fa-solid ${allExpanded ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true" />
                            {allExpanded ? 'Collapse all' : 'Expand all'}
                        </button>
                    </div>
                </div>
                <DataTable
                    value={unitRows}
                    dataKey="id"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    onRowClick={(e) => {
                        if (e.originalEvent.target.closest('.p-row-toggler')) return;
                        toggleUnit(e.data);
                    }}
                    sortMode="multiple"
                    multiSortMeta={multiSortMeta}
                    onSort={(e) => setMultiSortMeta(e.multiSortMeta?.length ? e.multiSortMeta : DEFAULT_SORT)}
                    rowClassName={(unit) => {
                        const tint = unitRowClassName(unit);
                        return { [tint]: !!tint, 'unit-row': true, 'is-expanded': !!expandedRows[unit.id] };
                    }}
                    filters={filters}
                    onFilter={(e) => setFilters(e.filters)}
                    filterDisplay="row"
                    className="gate-now-datatable"
                    scrollable
                    scrollHeight="flex"
                    emptyMessage={(
                        <div className="table-empty">
                            <i className="fa-solid fa-filter" aria-hidden="true" />
                            <span>No units match these filters</span>
                        </div>
                    )}
                    size="small"
                >
                    <Column expander style={{ width: '3rem', minWidth: '3rem' }} frozen />
                    <Column
                        header="Status"
                        field="TrustStatus"
                        sortable
                        sortField="SortRank"
                        filter
                        filterElement={DropdownFilterTemplate}
                        body={(u) => <TrustIcon trust={u.TrustStatus} />}
                        style={{ width: '7.5rem', minWidth: '7.5rem' }}
                        frozen
                    />
                    <Column
                        header="Status Description"
                        field="StatusLabel"
                        body={statusTextBody}
                        filter
                        filterElement={DropdownFilterTemplate}
                        sortable
                        style={{ minWidth: '14rem' }}
                    />
                    <Column
                        header="Tenant"
                        field="SQLTenantName"
                        body={(u) => <span className="unit-tenant">{u.SQLTenantName}</span>}
                        sortable
                        filter
                        filterElement={DropdownFilterTemplate}
                        style={{ minWidth: isCompact ? '8rem' : '11rem' }}
                        frozen={!isCompact}
                    />
                    <Column
                        header="Gates"
                        field="GateSummary"
                        body={gatesBody}
                        sortable
                        sortField="GateCount"
                        filter
                        filterField="GateNames"
                        filterElement={DropdownFilterTemplate}
                        style={{ minWidth: '11rem' }}
                    />
                    <Column
                        header="Unit"
                        field="SQLUnitName"
                        body={unitCodeBody}
                        sortable
                        filter
                        filterElement={DropdownFilterTemplate}
                        style={{ minWidth: '8rem' }}
                    />
                    <Column
                        header="Location"
                        field="SQLLocationName"
                        body={locationBody}
                        sortable
                        filter
                        filterElement={DropdownFilterTemplate}
                        style={{ minWidth: '9rem' }}
                    />
                    <Column
                        header="Last Sensor Reading"
                        field="LastAgeMinutes"
                        body={(u) => <SensorChip timestamp={u.LastTimestamp} />}
                        sortable
                        style={{ minWidth: '11rem' }}
                    />
                    <Column
                        header="Scheduled Open"
                        field="RawStartTimeString"
                        body={(u) => timeBody(u.RawStartTimeString)}
                        sortable
                        style={{ minWidth: '8rem' }}
                    />
                    <Column
                        header="Scheduled Close"
                        field="RawEndTimeString"
                        body={(u) => timeBody(u.RawEndTimeString)}
                        sortable
                        style={{ minWidth: '8rem' }}
                    />
                </DataTable>
            </div>
        </div>
    );
};
